import { useState } from "react";
import PhotoSticker from "./PhotoSticker";

interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

interface Entry {
  id: string;
  guest_name: string;
  message: string | null;
  emoji: string | null;
  pos_x: number;
  pos_y: number;
  signature_data: string | null;
  photo_id: string | null;
  status: string;
}

interface Props {
  photos: Photo[];
  entries: Entry[];
  coverPhotoUrl: string | null;
}

export default function PreviewTab({ photos, entries, coverPhotoUrl }: Props) {
  const [activePhotoId, setActivePhotoId] = useState<string | null>(
    photos.length > 0 ? photos[0].id : null,
  );
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);

  const visibleEntries = entries.filter((e) => e.status === "visible");

  const getEntriesForPhoto = (photoId: string) =>
    visibleEntries.filter((e) => e.photo_id === photoId);

  const freeEntries = visibleEntries.filter((e) => !e.photo_id);

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-amber-100 text-amber-400 mb-3">
          <i className="ri-image-2-line text-3xl"></i>
        </div>
        <p className="text-slate-500 font-medium">Sin fotos aún</p>
        <p className="text-slate-400 text-sm mt-1">Las fotos del libro aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Thumbnail rail */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {photos.map((photo) => {
          const count = getEntriesForPhoto(photo.id).length;
          return (
            <button
              key={photo.id}
              onClick={() => setActivePhotoId(photo.id)}
              className={`relative shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                activePhotoId === photo.id
                  ? "border-amber-400 scale-105"
                  : "border-transparent hover:border-amber-200"
              }`}
            >
              <img
                src={photo.photo_url}
                alt=""
                className="w-full h-full object-cover object-top"
              />
              {count > 0 && (
                <div className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-amber-500 rounded-full text-white text-[9px] font-bold">
                  {count}
                </div>
              )}
              {coverPhotoUrl === photo.photo_url && (
                <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-[8px] text-center py-0.5 font-bold">
                  Portada
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Active photo with stickers */}
      {activePhotoId && (
        <div className="relative rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
          {(() => {
            const photo = photos.find((p) => p.id === activePhotoId);
            if (!photo) return null;
            const photoEntries = getEntriesForPhoto(photo.id);
            return (
              <>
                <img
                  src={photo.photo_url}
                  alt=""
                  className="w-full object-cover object-top"
                  style={{ maxHeight: "480px" }}
                />
                {/* Stickers positioned over photo */}
                {photoEntries.map((entry) => (
                  <PhotoSticker
                    key={entry.id}
                    entry={entry}
                    onClick={() => setSelectedEntry(entry)}
                  />
                ))}
                {/* Count badge */}
                {photoEntries.length > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-message-3-line text-xs"></i>
                    {photoEntries.length} {photoEntries.length === 1 ? "mensaje" : "mensajes"}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Free entries (no photo) */}
      {freeEntries.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Mensajes sin foto específica
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {freeEntries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-amber-300 transition-all"
                onClick={() => setSelectedEntry(entry)}
              >
                <div className="flex items-center gap-2 mb-1">
                  {entry.emoji && <span>{entry.emoji}</span>}
                  <span className="font-semibold text-slate-700 text-sm">{entry.guest_name}</span>
                </div>
                {entry.message && (
                  <p className="text-slate-500 text-xs line-clamp-2">{entry.message}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entry detail modal */}
      {selectedEntry && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedEntry(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {selectedEntry.emoji && <span className="text-2xl">{selectedEntry.emoji}</span>}
                <div>
                  <p className="font-bold text-slate-800">{selectedEntry.guest_name}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedEntry(null)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {selectedEntry.message && (
              <p className="text-slate-600 text-sm leading-relaxed mb-4">&ldquo;{selectedEntry.message}&rdquo;</p>
            )}
            {selectedEntry.signature_data && (
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <p className="text-xs text-slate-400 mb-2">Firma</p>
                <img
                  src={selectedEntry.signature_data}
                  alt="firma"
                  className="w-full h-16 object-contain"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}